import AddRoundedIcon from '@mui/icons-material/AddRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Fab from '@mui/material/Fab';
import Paper from '@mui/material/Paper';
import { useTranslation } from 'react-i18next';

import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { useState } from 'react';

import type { PublicUser } from '@/lib/schemas/user';
import { useSession } from '@/features/auth/useSession';
import { can } from '@/features/business/capabilities';
import { useBusiness } from '@/features/business/useBusiness';
import { useLocation, useNavigate } from 'react-router-dom';

import { layout } from '@/theme';

import { visibleNavItems } from './navigation';

interface BottomNavProps {
  role?: PublicUser['role'];
  businessSlug?: string | null;
  capabilities?: string[];
  isSignedIn: boolean;
  onCompose: () => void;
}

/**
 * Mobile navigation (spec §9.3): Home / Search / Compose FAB / Notifications /
 * Profile. Signed-out visitors lose the auth-only destinations, so the compose
 * button is hidden too rather than leading to a dead end.
 */
export function BottomNav({
  isSignedIn,
  role,
  businessSlug,
  capabilities,
  onCompose,
}: BottomNavProps) {
  const { t } = useTranslation();
  // The same padlock the rail shows, for the same reason: a business on the
  // free plan cannot post as itself, and the button should say so before it
  // is pressed rather than after.
  const { user } = useSession();
  const isBusiness = user?.accountType === 'BUSINESS';
  const business = useBusiness(user?.businessSlug ?? '', isBusiness);
  const locked =
    isBusiness && business.data !== undefined && !can(business.data, 'POST_AS_BUSINESS');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const all = visibleNavItems({ isSignedIn, role, businessSlug, capabilities });
  const items = all.filter((item) => item.onBottomBar);
  // Everything the four slots cannot hold. Without this the whole business
  // side of the app — the inbox, the dashboard — simply did not exist on a
  // phone, which is where a shopkeeper actually reads their posts.
  const overflow = all.filter((item) => !item.onBottomBar);
  const [moreOpen, setMoreOpen] = useState(false);
  const current = items.findIndex((item) =>
    item.to === '/' ? pathname === '/' : pathname.startsWith(item.to),
  );

  return (
    <>
      {isSignedIn && (
        <Fab
          color="primary"
          aria-label={locked ? t('composer.postLocked') : t('composer.write')}
          onClick={onCompose}
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            right: 16,
            bottom: layout.bottomNavHeight + 16,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        >
          {locked ? <LockRoundedIcon /> : <AddRoundedIcon />}
        </Fab>
      )}

      <Paper
        elevation={0}
        square
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          borderTop: 1,
          borderColor: 'divider',
          // Keeps the bar clear of the iOS home indicator.
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <BottomNavigation
          showLabels
          value={current === -1 ? false : current}
          onChange={(_event, index: number) => {
            const target = items[index];
            if (target) navigate(target.to);
          }}
          // A minimum, not a height. A fixed one clipped the labels off the
          // bottom of the screen, and a translation with a taller label — or
          // somebody browsing at a larger font size — would do it again.
          sx={{ minHeight: layout.bottomNavHeight, height: 'auto', backgroundColor: 'transparent' }}
        >
          {items.map((item) => (
            <BottomNavigationAction
              key={item.to}
              label={t(item.labelKey)}
              icon={<item.Icon fontSize="small" />}
              sx={{
                minWidth: 0,
                py: 1,
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  // One line. Five items across a narrow phone is tight
                  // enough without "Notifications" taking two.
                  whiteSpace: 'nowrap',
                },
              }}
            />
          ))}

          {overflow.length > 0 && (
            <BottomNavigationAction
              label={t('nav.more')}
              icon={<MenuRoundedIcon fontSize="small" />}
              onClick={() => setMoreOpen(true)}
              sx={{
                minWidth: 0,
                py: 1,
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.75rem',
                  // One line. Five items across a narrow phone is tight
                  // enough without "Notifications" taking two.
                  whiteSpace: 'nowrap',
                },
              }}
            />
          )}
        </BottomNavigation>
      </Paper>

      {/* A sheet rather than a menu: these are destinations, the list is as
          long as the account is interesting, and a thumb reaches the bottom of
          a phone more easily than the top. */}
      <Drawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        slotProps={{ paper: { sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } } }}
      >
        <List sx={{ pb: 'env(safe-area-inset-bottom)' }} aria-label={t('nav.more')}>
          {overflow.map((item) => (
            <ListItemButton
              key={item.to}
              selected={pathname.startsWith(item.to.split('?')[0] ?? item.to)}
              onClick={() => {
                setMoreOpen(false);
                navigate(item.to);
              }}
            >
              <ListItemIcon>
                <item.Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t(item.labelKey)} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  );
}
